import { useController } from '@aha-app/mvc';
import { type fabric } from 'fabric';
import React, { useEffect, useRef, useState } from 'react';
import { type Instance as TippyInstance } from 'tippy.js/index';
import styles from '../businessProcess/businessProcess.module.css';
import {
  featuredShapes,
  groupedShapes,
  type BusinessProcessProperties,
  type BusinessProcessShapeNames,
} from '../businessProcess/definitions';
import {
  BusinessProcessCategoryIcon,
  BusinessProcessIcon,
  BusinessProcessTypeIcon,
} from '../businessProcess/icons';
import WhiteboardController from '../controllers/WhiteboardController';
import { isBusinessProcessShape, isSimpleSelection } from '../objects/guards';
import type Shape from '../objects/Shape';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import WithHint from '../toolbars/WithHint';
import Tool, {
  type IToolAvailableInput,
  type ToolButtonComponent,
} from './Tool';

interface VariantId {
  shapeName: BusinessProcessShapeNames;
  variant: string;
}

interface PopupProps {
  current: VariantId;
  convertShapes: (attrs: VariantId) => void;
  options: (VariantId & {
    name: string;
    properties?: BusinessProcessProperties;
  })[];
}

const Popup: React.FC<PopupProps> = ({ convertShapes, current, options }) => {
  return (
    <ToolbarMenu
      columns={Math.max(5, Math.min(options.length / 2, 9))}
      columnWidth={48}
    >
      {options.map(({ shapeName, variant, name }) => (
        <WithHint key={variant} name={name}>
          <ToolButton
            active={
              variant === current.variant && shapeName === current.shapeName
            }
            onClick={() => convertShapes({ shapeName, variant })}
            className={styles.businessProcessLargeButton}
          >
            <BusinessProcessIcon shapeName={shapeName} variant={variant} />
          </ToolButton>
        </WithHint>
      ))}
    </ToolbarMenu>
  );
};

export const doShapeConversion = (
  controller: WhiteboardController,
  selection: fabric.Object,
  attrs: VariantId
) => {
  const objects = selection.getObjects().filter(isBusinessProcessShape);

  for (const obj of objects) {
    obj.set(attrs);
    // reinitalize the shape to sync over the new shape/variant's properties
    obj.initializeShape();
  }

  // adjust the selection dimensions as the shapes may have changed size
  if (isSimpleSelection(selection)) {
    selection.syncFromObjects();
  }

  ToolBusinessProcessType.toolActionComplete({ controller });
};

interface Props {
  selection: fabric.Object;
  name: string;
  category: boolean;
}
const BusinessProcessChangeButton: React.FC<Props> = ({
  selection,
  name,
  category,
}) => {
  const controller = useController(WhiteboardController);
  const obj = selection.firstObject();
  const { shapeName, variant } = obj;
  const [current, setCurrent] = useState({ shapeName, variant });

  const tippyRef = useRef<TippyInstance>();

  useEffect(() => {
    setCurrent({ shapeName, variant });
  }, [shapeName, variant]);

  const convertShapes = (attrs: VariantId) => {
    tippyRef.current?.hide();
    setCurrent(attrs);

    doShapeConversion(controller, selection, attrs);
    // refresh the other toolbar button
    controller.actionShowObjectToolbar();
  };

  const options = category
    ? featuredShapes
    : // find the group that contains this shape (should always be one);
      groupedShapes.find(group =>
        group.shapes.some(
          s => s.shapeName === shapeName && s.variant === variant
        )
      )?.shapes ?? [];

  return (
    <ToolButton
      popup={
        <Popup
          convertShapes={convertShapes}
          current={current}
          options={options}
        />
      }
      maxWidth={500}
      tippyRef={tippyRef}
      id='shapeChange'
      name={name}
    >
      {category ? (
        <BusinessProcessCategoryIcon
          shapeName={current.shapeName}
          variant={current.variant}
        />
      ) : (
        <BusinessProcessTypeIcon
          shapeName={current.shapeName}
          variant={current.variant}
        />
      )}
    </ToolButton>
  );
};

// only available if selection is all the same business process shape
const canConvert = (selection?: fabric.Object | null) => {
  const objects = selection?.getObjects();
  return !!objects?.every(
    obj =>
      isBusinessProcessShape(obj) &&
      obj.shapeName === (objects[0] as Shape).shapeName
  );
};

const BusinessProcessTypeButton: ToolButtonComponent = ({ selection }) => (
  <BusinessProcessChangeButton
    selection={selection}
    category={false}
    name={ToolBusinessProcessType.toolName}
  />
);

export class ToolBusinessProcessType extends Tool {
  static toolName = 'Change business process';

  static available({ selection }: IToolAvailableInput): boolean {
    return canConvert(selection);
  }

  static buttonComponent() {
    return BusinessProcessTypeButton;
  }
}

const BusinessProcessCategoryButton: ToolButtonComponent = ({ selection }) => (
  <BusinessProcessChangeButton
    selection={selection}
    category={true}
    name={ToolBusinessProcessCategory.toolName}
  />
);

export class ToolBusinessProcessCategory extends Tool {
  static toolName = 'Change base shape';

  static available({ selection }: IToolAvailableInput): boolean {
    return canConvert(selection);
  }

  static buttonComponent() {
    return BusinessProcessCategoryButton;
  }
}
